/* global window console document Office*/

import * as React from "react";
import { makeStyles } from "@fluentui/react-components";
import { getCurrentDocumentPath, setupSelectionChangeListener } from "../word";
import LoadingScreen from "./LoadingScreen";
import ErrorScreen from "./ErrorScreen";
import { handleRemoteAction, registerAction } from "../remoteActions";
import { debugAction } from "../actions/debug";
import { changeAction } from "../actions/parseChanges";
import { saveAction } from "../actions/save";

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  iframe: {
    width: "100%",
    height: "100%",
    border: "none",
    flex: "1 1 0",
  },
});

const apiUrls = {
  local: "http://localhost:4200/",
  dev: "https://dev.silvernova.ai/",
  prod: "https://app.silvernova.ai/",
};

const App: React.FC = () => {
  const styles = useStyles();
  const [documentId, setDocumentId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [realm, setRealm] = React.useState<string | null>(null);
  const [url, setUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    getCurrentDocumentPath().then((path) => {
      console.log(path);
      if (
        !path.startsWith("http://localhost:1337/") &&
        !path.startsWith("https://api.associate") &&
        !path.startsWith("https://api.dev.silvernova.ai") &&
        !path.startsWith("https://api.silvernova.ai")
      ) {
        // It's not an associate file
        setLoading(false);
        return;
      }

      const parts = path.split("/");
      const potentialId = parts[parts.length - 2];
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

      const realmIndex = parts.indexOf("webdav") + 1;
      const realm = parts[realmIndex];
      if (realm) {
        setRealm(realm);
      }

      if (uuidRegex.test(potentialId) && realm) {
        setDocumentId(potentialId);
        let environment: "local" | "dev" | "prod" = "local";
        if (path.startsWith("http://localhost:1337/")) {
          environment = "local";
        } else if (
          path.startsWith("https://api.associate-dev.silberfluss.io/") ||
          path.startsWith("https://api.dev.silvernova.ai/")
        ) {
          environment = "dev";
        } else if (
          path.startsWith("https://api.associate.silberfluss.io/") ||
          path.startsWith("https://api.silvernova.ai/")
        ) {
          environment = "prod";
        }
        const potentialToken = encodeURIComponent(parts[parts.length - 3]);
        setUrl(`${apiUrls[environment]}_addin/${realm}/${potentialId}?token=${potentialToken}`);
      }
      setLoading(false);
    });

    registerAction(debugAction);
    registerAction(changeAction);
    registerAction(saveAction);

    // Set up the selection change listener
    Office.onReady(() => {
      setupSelectionChangeListener((selectedText) => {
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: "textSelected",
              text: selectedText,
            },
            "*"
          );
        }
      });
    });

    window.addEventListener("message", (event) => {
      if (event.data === "sessionExpired") {
        setLoading(false);
        setError("Die Sitzung ist abgelaufen. Sie müssen das Dokument schließen und erneut öffnen, um fortzufahren.");
        return;
      } else if (event.data === "missingCanvas") {
        setLoading(false);
        setError(
          "Das Dokument konnte nicht bearbeitet werden, da es nicht durch den Silvernova Associate erstellt worden ist."
        );
        return;
      }
      handleRemoteAction(event);
    });
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!documentId) {
    if (!realm) {
      return (
        <ErrorScreen
          title="Kein Silvernova-Dokument"
          message="Das Dokument kann nicht genutzt werden, da es nicht im Silvernova Associate gespeichert ist."
          type="warning"
        />
      );
    }
    return <ErrorScreen title="Fehler" message="Das Dokument konnte nicht gefunden werden." type="error" />;
  }

  if (!url) {
    return <ErrorScreen title="Fehler" message="Die Ziel-URL konnte nicht generiert werden." type="warning" />;
  }

  if (error) {
    return <ErrorScreen title="Fehler" message={error} type="error" />;
  }

  return (
    <div className={styles.root}>
      <iframe title="Silvernova Associate" src={url} className={styles.iframe}></iframe>
    </div>
  );
};

export default App;
